import { CheckCircle, HelpCircle } from 'lucide-react'
import { useState } from 'react'

import ROUTES from '@/ROUTES'
import { api } from '@/services/api.service'

const CATEGORIES = ['Help Request', 'Feedback', 'Report an Issue'] as const

export default function HelpPage() {
    const [category, setCategory] = useState<string>(CATEGORIES[0])
    const [subject, setSubject] = useState('')
    const [message, setMessage] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [error, setError] = useState<string | null>(null)

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault()
        setError(null)
        setSubmitting(true)

        try {
            const response = await api(ROUTES.app.helpRequest, { category, subject, message })
            if (response.error) {
                setError(response.error)
            } else {
                setSubmitted(true)
            }
        } catch {
            setError('Something went wrong. Please try again.')
        } finally {
            setSubmitting(false)
        }
    }

    if (submitted) {
        return (
            <div className='flex flex-col items-center justify-center py-16 px-4'>
                <div className='bg-white rounded-xl shadow-sm border border-neutral-200 p-8 max-w-md w-full text-center'>
                    <CheckCircle className='w-12 h-12 text-green-500 mx-auto mb-4' />
                    <h2 className='text-xl font-semibold text-gray-900 mb-2'>Message Sent</h2>
                    <p className='text-gray-600 mb-6'>
                        Thank you for reaching out. We'll get back to you as soon as possible.
                    </p>
                    <button
                        onClick={() => {
                            setSubmitted(false)
                            setSubject('')
                            setMessage('')
                        }}
                        className='text-brand-600 hover:text-brand-700 font-medium'
                    >
                        Send another message
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className='max-w-2xl mx-auto py-8 px-4'>
            <div className='flex items-center gap-3 mb-6'>
                <HelpCircle className='w-7 h-7 text-brand-600' />
                <h1 className='text-2xl font-bold text-gray-900'>Help & Feedback</h1>
            </div>
            <p className='text-gray-600 mb-8'>
                Need help, have feedback, or want to report an issue? Send us a message and we'll get back to you.
            </p>

            <form onSubmit={handleSubmit} className='bg-white rounded-xl shadow-sm border border-neutral-200 p-6 space-y-5'>
                <div>
                    <label htmlFor='category' className='block text-sm font-medium text-gray-700 mb-1'>
                        Category
                    </label>
                    <select
                        id='category'
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className='w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500'
                    >
                        {CATEGORIES.map((cat) => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label htmlFor='subject' className='block text-sm font-medium text-gray-700 mb-1'>
                        Subject
                    </label>
                    <input
                        id='subject'
                        type='text'
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        required
                        placeholder='Brief description of your request'
                        className='w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500'
                    />
                </div>

                <div>
                    <label htmlFor='message' className='block text-sm font-medium text-gray-700 mb-1'>
                        Message
                    </label>
                    <textarea
                        id='message'
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                        rows={6}
                        placeholder='Describe your issue, feedback, or question in detail...'
                        className='w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 resize-y'
                    />
                </div>

                {error && (
                    <div className='p-3 bg-red-50 border border-red-200 rounded-lg'>
                        <p className='text-sm text-red-700'>{error}</p>
                    </div>
                )}

                <button
                    type='submit'
                    disabled={submitting || !subject.trim() || !message.trim()}
                    className='w-full bg-brand-600 text-white py-2.5 px-4 rounded-lg font-medium text-sm hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                    {submitting ? 'Sending...' : 'Send Message'}
                </button>
            </form>
        </div>
    )
}
